import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { CodyIconBadge } from '../components/CodyLogo';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Mail, ArrowRight, RefreshCw } from 'lucide-react';
import api from '../lib/api';
import analytics from '../lib/tracking';
import { toast } from 'sonner';

export default function VerifyEmail() {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setLoading(true);
    try {
      await api.request('/api/auth/verify-email', { method: 'POST', body: JSON.stringify({ code }) });
      toast.success('Email verified!');
      analytics.emailVerified(user?.email);
      // Reload user data then navigate
      window.location.href = '/onboard';
    } catch (err) {
      toast.error(err.message || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await api.request('/api/auth/resend-verification', { method: 'POST' });
      toast.success('New code sent! Check your inbox.');
    } catch (err) {
      toast.error(err.message || 'Failed to resend');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4">
      <div className="absolute top-0 left-0 right-0 h-[40vh] hero-gradient" />

      <Card className="relative w-full max-w-md p-8 rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.06)] border-[#E6E8EE]">
        <div className="flex items-center justify-center mb-6">
          <CodyIconBadge size={44} />
        </div>

        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FFF2EA, #FFE8DE)' }}>
            <Mail className="w-5 h-5 text-[#FF4500]" />
          </div>
          <h2 className="text-xl font-bold mb-1" style={{ fontFamily: "'Space Grotesk'" }}>Check your email</h2>
          <p className="text-sm text-[#5B667A]">
            We sent a 6-digit verification code to<br />
            <span className="font-semibold text-[#0B0F17]">{user?.email || 'your email'}</span>
          </p>
        </div>

        <form onSubmit={handleVerify} className="space-y-4">
          <Input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            className="h-14 text-center text-2xl font-bold tracking-[0.5em] rounded-xl"
            style={{ fontFamily: "'Space Grotesk'" }}
            data-testid="verify-code-input"
            autoFocus
          />
          <Button
            type="submit"
            disabled={loading || code.length !== 6}
            className="w-full h-11 rounded-xl font-semibold text-sm"
            style={{ background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' }}
            data-testid="verify-submit-button"
          >
            {loading ? 'Verifying...' : 'Verify Email'} {!loading && <ArrowRight className="ml-2 w-4 h-4" />}
          </Button>
        </form>

        <div className="mt-5 text-center space-y-3">
          <button
            onClick={handleResend}
            disabled={resending}
            className="text-xs text-[#FF4500] font-semibold hover:underline flex items-center justify-center gap-1 mx-auto"
          >
            <RefreshCw className={`w-3 h-3 ${resending ? 'animate-spin' : ''}`} />
            {resending ? 'Sending...' : "Didn't get the code? Resend"}
          </button>
          <button onClick={logout} className="text-xs text-[#9ca3af] hover:text-[#5B667A]">
            Sign out
          </button>
        </div>
      </Card>
    </div>
  );
}
